import React, { useState, useEffect } from "react";
import { Modal, Input, Select, message } from "antd";
import PageLayout from "@components/pageLayout/PageLayout.react";
import api from "@/libs/api";
import "./userManagement.css";

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

export default function UserManagement() {
  const roleType = localStorage.getItem("roleType") || "";
  const isSuperadmin = roleType === "SUPERADMIN";
  const userPermissions = (() => {
    try { return JSON.parse(localStorage.getItem("permissions") || "[]"); }
    catch { return []; }
  })();
  const canCreate = isSuperadmin || userPermissions.includes("user:create");
  const canEdit = isSuperadmin || userPermissions.includes("user:update");

  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showLoginStats, setShowLoginStats] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tempPassword, setTempPassword] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: "",
    firstName: "",
    lastName: "",
    companyId: undefined,
    roleId: undefined,
  });
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    contactNumber: "",
    companyId: undefined,
    roleId: undefined,
    status: "ACTIVE",
  });
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/flows/users");
      if (res.data.success) {
        setUsers(res.data.data || []);
        setShowLoginStats(res.data.show_login_stats || false);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchFormData = async () => {
    try {
      const [companiesRes, rolesRes] = await Promise.all([
        api.get("/api/companies?include_all=true"),
        api.get("/api/flows/roles"),
      ]);
      if (companiesRes.data.success) setCompanies(companiesRes.data.data || []);
      if (rolesRes.data.success) setRoles(rolesRes.data.data || []);
    } catch (err) {
      console.error("Failed to fetch form data:", err);
    }
  };

  // === Create User ===
  const handleOpenCreate = () => {
    setNewUser({ email: "", firstName: "", lastName: "", companyId: undefined, roleId: undefined });
    setTempPassword(null);
    fetchFormData();
    setShowCreateModal(true);
  };

  const handleCreate = async () => {
    if (!newUser.email.trim()) {
      message.error("Email is required");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/api/flows/users", {
        email: newUser.email.trim(),
        firstName: newUser.firstName.trim(),
        lastName: newUser.lastName.trim(),
        companyId: newUser.companyId,
        roleId: newUser.roleId,
      });
      if (res.data.success) {
        const tp = res.data.data?.tempPassword;
        if (tp) {
          setTempPassword(tp);
          message.success("User created — temporary password shown below");
        } else {
          message.success("User created successfully");
          setShowCreateModal(false);
        }
        fetchUsers();
      } else {
        message.error(res.data.message || "Failed to create user");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  // === Edit User ===
  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      firstName: user.first_name || "",
      lastName: user.last_name || "",
      contactNumber: user.contact_number || "",
      companyId: user.company_id || undefined,
      roleId: user.role_id || undefined,
      status: user.status || "ACTIVE",
    });
    fetchFormData();
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const res = await api.put(`/api/flows/users/${encodeURIComponent(editingUser.id)}`, {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        contactNumber: editForm.contactNumber.trim(),
        companyId: editForm.companyId,
        roleId: editForm.roleId,
        status: editForm.status,
      });
      if (res.data.success) {
        message.success("User updated successfully");
        setShowEditModal(false);
        setEditingUser(null);
        fetchUsers();
      } else {
        message.error(res.data.message || res.data.error || "Failed to update user");
      }
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const getRoleTypeBadgeClass = (roleType) => {
    const t = (roleType || "").toUpperCase();
    if (t === "SUPERADMIN") return "um-role-badge superadmin";
    if (t === "INTERNAL") return "um-role-badge internal";
    if (t === "PARTNER") return "um-role-badge partner";
    if (t === "AGGREGATOR") return "um-role-badge aggregator";
    return "um-role-badge";
  };

  const getStatusClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "active") return "um-status-badge active";
    if (s === "pending") return "um-status-badge pending";
    return "um-status-badge inactive";
  };

  const formatDate = (ts) => {
    if (!ts) return "\u2014";
    const d = new Date(ts);
    return d.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  };

  const filteredUsers = searchQuery.trim()
    ? users.filter((u) => {
        const q = searchQuery.trim().toLowerCase();
        return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
      })
    : users;

  return (
    <PageLayout active="userManagement">
      <div className="um-page">
        <div className="um-header">
          <h1 className="um-title">User Management</h1>
          <p className="um-subtitle">Manage admin users and their roles</p>
        </div>

        <div className="um-table-section">
          <div className="um-table-header">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <h2 className="um-section-title">All Users</h2>
              <span className="um-count">
                {filteredUsers.length}{searchQuery.trim() ? ` of ${users.length}` : ""} user{users.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                style={{ width: 240, height: 39 }}
              />
              {canCreate && (
                <button className="um-btn-add" onClick={handleOpenCreate}>
                  + Create User
                </button>
              )}
            </div>
          </div>
          {loading ? (
            <div className="um-loading">Loading...</div>
          ) : (
            <div className="um-table-wrapper">
              <table className="um-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Company</th>
                    <th>Status</th>
                    {showLoginStats && <th>Last Login</th>}
                    {showLoginStats && <th>Total Logins</th>}
                    {showLoginStats && <th>Last Login IP</th>}
                    {canEdit && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="um-td-name">{user.name || "\u2014"}</td>
                      <td>{user.email || "\u2014"}</td>
                      <td>
                        <span className={getRoleTypeBadgeClass(user.role_type)}>
                          {user.role_name || user.role_type || "\u2014"}
                        </span>
                      </td>
                      <td>{user.company_name || "\u2014"}</td>
                      <td>
                        <span className={getStatusClass(user.status)}>
                          {user.status || "\u2014"}
                        </span>
                      </td>
                      {showLoginStats && <td>{formatDate(user.last_login_at)}</td>}
                      {showLoginStats && <td>{user.total_logins ?? "\u2014"}</td>}
                      {showLoginStats && <td className="um-td-ip">{user.last_login_ip || "\u2014"}</td>}
                      {canEdit && (
                        <td>
                          <div className="um-actions">
                            {(user.role_type || "").toUpperCase() !== "SUPERADMIN" && (
                              <button className="um-btn-edit" onClick={() => handleOpenEdit(user)}>
                                Edit
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={5 + (showLoginStats ? 3 : 0) + (canEdit ? 1 : 0)} className="um-empty">
                        {searchQuery.trim() ? "No users match your search" : "No users found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      <Modal
        title={tempPassword ? "User Created" : "Create User"}
        open={showCreateModal}
        onCancel={() => { setShowCreateModal(false); setTempPassword(null); }}
        onOk={tempPassword ? () => { setShowCreateModal(false); setTempPassword(null); } : handleCreate}
        okText={tempPassword ? "Done" : "Create"}
        confirmLoading={creating}
      >
        {tempPassword ? (
          <div className="um-temp-password">
            <p>User has been created. Share this temporary password with the user:</p>
            <div className="um-password-box">
              <code>{tempPassword}</code>
            </div>
            <p className="um-password-hint">This password will not be shown again.</p>
          </div>
        ) : (
          <div className="um-form">
            <div className="um-form-group">
              <label>Email *</label>
              <Input
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@company.com"
              />
            </div>
            <div className="um-form-row">
              <div className="um-form-group">
                <label>First Name</label>
                <Input
                  value={newUser.firstName}
                  onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                  placeholder="First name"
                />
              </div>
              <div className="um-form-group">
                <label>Last Name</label>
                <Input
                  value={newUser.lastName}
                  onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                  placeholder="Last name"
                />
              </div>
            </div>
            <div className="um-form-group">
              <label>Company</label>
              <Select
                value={newUser.companyId}
                onChange={(value) => setNewUser({ ...newUser, companyId: value })}
                placeholder="Select company"
                allowClear
                style={{ width: "100%" }}
                options={companies.map((c) => ({ value: c.id, label: c.name || c.company_name }))}
              />
            </div>
            <div className="um-form-group">
              <label>Role</label>
              <Select
                value={newUser.roleId}
                onChange={(value) => setNewUser({ ...newUser, roleId: value })}
                placeholder="Select role"
                allowClear
                style={{ width: "100%" }}
                options={roles.map((r) => ({ value: r.id, label: r.role_name }))}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title={`Edit User \u2014 ${editingUser?.email || ""}`}
        open={showEditModal}
        onCancel={() => { setShowEditModal(false); setEditingUser(null); }}
        onOk={handleSaveEdit}
        okText="Save"
        confirmLoading={saving}
        width={520}
      >
        <div className="um-form">
          <div className="um-form-group">
            <label>Email</label>
            <Input value={editingUser?.email || ""} disabled />
          </div>
          <div className="um-form-row">
            <div className="um-form-group">
              <label>First Name</label>
              <Input
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                placeholder="First name"
              />
            </div>
            <div className="um-form-group">
              <label>Last Name</label>
              <Input
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                placeholder="Last name"
              />
            </div>
          </div>
          <div className="um-form-group">
            <label>Contact Number</label>
            <Input
              value={editForm.contactNumber}
              onChange={(e) => setEditForm({ ...editForm, contactNumber: e.target.value })}
              placeholder="Contact number"
            />
          </div>
          <div className="um-form-group">
            <label>Company</label>
            <Select
              value={editForm.companyId}
              onChange={(value) => setEditForm({ ...editForm, companyId: value })}
              placeholder="Select company"
              allowClear
              style={{ width: "100%" }}
              options={companies.map((c) => ({ value: c.id, label: c.name || c.company_name }))}
            />
          </div>
          <div className="um-form-group">
            <label>Role</label>
            <Select
              value={editForm.roleId}
              onChange={(value) => setEditForm({ ...editForm, roleId: value })}
              placeholder="Select role"
              allowClear
              style={{ width: "100%" }}
              options={roles.map((r) => ({ value: r.id, label: r.role_name }))}
            />
          </div>
          <div className="um-form-group">
            <label>Status</label>
            <Select
              value={editForm.status}
              onChange={(value) => setEditForm({ ...editForm, status: value })}
              style={{ width: "100%" }}
              options={STATUS_OPTIONS}
            />
          </div>
        </div>
      </Modal>

    </PageLayout>
  );
}
